import { detectDisconnectedPatents } from './detect-disconnected-patents'
import { getParentIds } from './get-parent-ids'
import { mergePatentRecords } from './merge-patent-records'
import { processPriorityClaims } from './process-priority-claims'
import { removeCyclicalConnections } from './remove-cyclical-connections'
import type {
  Connection,
  FamilyResult,
  ProcessedFamilyNode,
  ProcessedFamilyTree,
} from '..'

/**
 * Processes a list of family results to construct a family tree.
 *
 * This function merges patent records, processes priority claims, and constructs
 * nodes and connections to represent the family tree. It identifies level 1 nodes
 * (those without parents) and creates connections between nodes based on parent-child
 * relationships and priority claims.
 *
 * @param {FamilyResult[]} results - The list of family results to process.
 * @returns {ProcessedFamilyTree} An object containing nodes and connections representing the family tree.
 */
export function processFamily(results: FamilyResult[]): ProcessedFamilyTree {
  const nodes: ProcessedFamilyNode[] = []
  const connections: Connection[] = []
  const mergedRecords = mergePatentRecords(results)

  const level1Nodes = mergedRecords.filter((result) => {
    const parentIds = getParentIds(result, mergedRecords, results)
    return parentIds.length === 0
  })

  const priorityClaims = processPriorityClaims(
    level1Nodes.flatMap((result) => result.priorityClaims ?? [])
  )
  for (const claim of priorityClaims) {
    nodes.push({
      id: claim.documentNumber,
      data: {
        documentNumber: claim.documentNumber,
        country: claim.country,
        applicationNumber: undefined,
        applicationCountry: undefined,
        publishNumber: undefined,
        publishCountry: undefined,
        priorityDate: undefined,
        publishedDate: claim.documentDate,
        issuedDate: undefined,
      },
      parentIds: [],
      level: 0,
      nodeType: 'priority',
    })
  }

  for (const result of mergedRecords) {
    const parentReferences = getParentIds(result, mergedRecords, results)
    const parentIds = parentReferences.map((reference) => reference.id)
    const baseLevel = parentIds.length > 0 ? 2 : 1

    // Creates a publication node if the patent has a publication or is not yet granted.
    if (!result.issuedDate || result.publishNumber) {
      const publicationNode: ProcessedFamilyNode = {
        id: result.publishNumber ?? result.documentNumber,
        data: {
          documentNumber: result.publishNumber ?? result.documentNumber,
          country: result.publishCountry ?? result.country,
          applicationNumber: result.applicationNumber,
          applicationCountry: result.applicationCountry,
          publishNumber: result.publishNumber,
          publishCountry: result.publishCountry,
          priorityDate: result.minPriorityDate,
          publishedDate: result.publishedDate,
          issuedDate: undefined,
        },
        patent: result,
        parentIds,
        level: baseLevel,
        nodeType: 'publication',
      }
      nodes.push(publicationNode)

      // Creates a connected grant node for patents that have been issued.
      if (result.issuedDate) {
        const grantNode: ProcessedFamilyNode = {
          id: result.documentNumber,
          data: {
            documentNumber: result.documentNumber,
            country: result.country,
            applicationNumber: result.applicationNumber,
            applicationCountry: result.applicationCountry,
            publishNumber: result.publishNumber,
            publishCountry: result.publishCountry,
            priorityDate: result.minPriorityDate,
            publishedDate: result.publishedDate,
            issuedDate: result.issuedDate,
          },
          patent: result,
          parentIds: [publicationNode.id],
          level: baseLevel + 1,
          nodeType: 'grant',
        }
        nodes.push(grantNode)
        connections.push({ source: publicationNode.id, target: grantNode.id })
      }

      // Creates connections from parent nodes to this publication node.
      if (parentReferences.length > 0) {
        for (const parentReference of parentReferences) {
          connections.push({
            source: parentReference.id,
            target: publicationNode.id,
            label: parentReference.relationType,
          })
        }
      }

      // Creates connections from priority claims to this publication node.
      if (baseLevel === 1) {
        for (const claim of priorityClaims) {
          connections.push({
            source: claim.documentNumber,
            target: publicationNode.id,
          })
        }
      }
    } else {
      // Creates a standalone grant node for patents that were granted without publication.
      const grantNode: ProcessedFamilyNode = {
        id: result.documentNumber,
        data: {
          documentNumber: result.documentNumber,
          country: result.country,
          applicationNumber: result.applicationNumber,
          applicationCountry: result.applicationCountry,
          publishNumber: undefined,
          publishCountry: undefined,
          priorityDate: result.minPriorityDate,
          publishedDate: undefined,
          issuedDate: result.issuedDate,
        },
        patent: result,
        parentIds,
        level: baseLevel,
        nodeType: 'grant',
      }
      nodes.push(grantNode)

      // Creates connections from parent nodes to this grant node.
      if (parentReferences.length > 0) {
        for (const parentReference of parentReferences) {
          connections.push({
            source: parentReference.id,
            target: grantNode.id,
            label: parentReference.relationType,
          })
        }
      }

      // Creates connections from priority claims to this grant node.
      if (baseLevel === 1) {
        for (const claim of priorityClaims) {
          connections.push({
            source: claim.documentNumber,
            target: grantNode.id,
          })
        }
      }
    }
  }

  const nodeIds = new Set(nodes.map((node) => node.id))
  const filteredConnections = connections.filter(
    (connection) =>
      nodeIds.has(connection.source) &&
      nodeIds.has(connection.target) &&
      connection.source !== connection.target
  )

  const responseConnections = removeCyclicalConnections(
    filteredConnections,
    nodes
  )

  detectDisconnectedPatents(nodes, responseConnections)

  return { nodes, connections: responseConnections }
}
